import { AuthForm } from "../components/AuthForm";
import { useUserVerificationQuery, userVerificationService } from "@/common/api/services/userVerification";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Lock } from "lucide-react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import forgotPasswordImage from "@/common/assets/images/forgotPassword.webp";


type ResetPasswordFormValues = {
  code: string;
  newPassword: string;
  confirmPassword: string;
};

export const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const code = searchParams.get('code');

  useEffect(() => {
    if (!code) {
      navigate('/auth/login');
    }
  }, [code, navigate]);

  const resetPasswordSchema = z.object({
    newPassword: z.string()
      .min(8, t('ResetPasswordForm.validation.passwordMinLength'))
      .regex(/[A-Z]/, t('ResetPasswordForm.validation.passwordUppercase'))
      .regex(/[a-z]/, t('ResetPasswordForm.validation.passwordLowercase'))
      .regex(/[0-9]/, t('ResetPasswordForm.validation.passwordNumber'))
      .regex(/[^A-Za-z0-9]/, t('ResetPasswordForm.validation.passwordSpecial')),
    confirmPassword: z.string()
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('ResetPasswordForm.validation.passwordsMatch'),
    path: ["confirmPassword"],
  });

  const methods = useForm<Omit<ResetPasswordFormValues, 'code'>>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { mutate: resetPassword, isPending } = useMutation({
    mutationFn: async (data: Omit<ResetPasswordFormValues, 'code'>) => {
      if (!code) throw new Error('Reset code is missing');
      return userVerificationService.resetPassword({
        code,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword
      });
    },
    onSuccess: () => {
      toast({
        title: t('toast.successTitle'),
        description: t('toast.successDescription'),
      });
      navigate('/auth/login');
    },
    onError: (error) => {
      toast({
        title: t('toast.errorTitle'),
        description: t('toast.errorDescription'),
        variant: "destructive",
      });
      console.error(error);
    },
  });

  const onSubmit = (data: Omit<ResetPasswordFormValues, 'code'>) => {
    resetPassword(data);
  };

  return (
    <AuthForm image={forgotPasswordImage}>
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">{t('ResetPasswordForm.headerTitle')}</h1>
        <p className="text-balance text-sm text-muted-foreground">
          {t('ResetPasswordForm.headerDescription')}
        </p>
      </div>
      <FormProvider {...methods}>
        <div className="space-y-6">
          <div className="flex flex-col gap-6">
            <FormField
              control={methods.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ResetPasswordForm.newPassword')}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={methods.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ResetPasswordForm.confirmPassword')}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={isPending}
              className="w-full"
              onClick={methods.handleSubmit(onSubmit)}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('ResetPasswordForm.resetLoading')}
                </>
              ) : (
                <>
                  {t('ResetPasswordForm.resetButton')}
                  <Lock className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </FormProvider>
    </AuthForm>
  );
}; 